
import {
   LoginActionTypes, ModalActionTypes, AuthAction
} from "./authAction";
import { CognitoUserSession } from "amazon-cognito-identity-js";

interface AuthState {
   activeModal: string | null;
   email: string;
   password: string;
   code: string;
   newPassword: string;
   passwordError: string | null;
   emailError: string | null;
   isEmailValid: boolean;
   isAuthenticated: boolean;
   passwordResetFlow: boolean;
   newPasswordFlow: boolean;

   user: {
      firstName: string,
      lastName: string,
   },

   resendTimer: number;
   canResend: boolean;

   loading: boolean;
   error: string | null;

   isSessionLoading: boolean;
   userSession: CognitoUserSession | null;
}

const initialState: AuthState = {
   activeModal: null,

   email: '',
   password: '',
   code: '',
   newPassword: '',
   passwordError: null,
   emailError: null,
   isEmailValid: false,
   isAuthenticated: false,
   passwordResetFlow: false,
   newPasswordFlow: false,

   user: {
      firstName: '',
      lastName: '',
   },

   resendTimer: 0,
   canResend: true,

   loading: false,
   error: null,

   isSessionLoading: true,
   userSession: null
}

const validateEmail = (email: string): boolean => {
   const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
   return re.test(email);
};

export const authReducer = (state = initialState, action: AuthAction): AuthState => {
   switch (action.type) {
      case ModalActionTypes.OPEN_CHOOSER:
         return { ...state, activeModal: 'chooser' };
      case ModalActionTypes.OPEN_LOGIN:
         return { ...state, activeModal: 'login', email: '' };
      case ModalActionTypes.OPEN_SIGN_UP:
         return { ...state, activeModal: 'signUp' };

      case ModalActionTypes.OPEN_NAME_SURNAME:
         return { ...state, activeModal: 'nameSurname' };

      case ModalActionTypes.OPEN_PASSWORD_SET:
         return { ...state, activeModal: 'setPassword', newPasswordFlow: state.passwordResetFlow, password: '' };

      case ModalActionTypes.OPEN_CODE:
         return { ...state, activeModal: 'smsCode' };

      case ModalActionTypes.OPEN_PASSWORD_LOGIN:
         return { ...state, activeModal: 'confirmPassword', password: '' };

      case ModalActionTypes.OPEN_FORGOT_PASSWORD:
         return { ...state, activeModal: 'forgotPassword', email: "" }

      case ModalActionTypes.CLOSE_MODAL:
         return {
            ...state,
            activeModal: null,
            passwordResetFlow: false,
            newPasswordFlow: false,
            password: "",
            email: '',
            code: '',
            newPassword: '',
         };

      case LoginActionTypes.EMAIL_CHANGED: {
         const newEmail = action.payload.email;
         const isValid = validateEmail(newEmail);

         return {
            ...state,
            email: newEmail,
            isEmailValid: isValid,
            emailError: (newEmail.length > 0 && !isValid) ? 'Некорректный email адрес (пример: "example@gmail.com")' : null
         }
      }
      case LoginActionTypes.PASSWORD_CHANGED:
         return { ...state, password: action.payload.password };

      case LoginActionTypes.TIMER_TICK:
         if (state.resendTimer <= 1) {
            return { ...state, resendTimer: 0, canResend: true }
         }
         return { ...state, resendTimer: state.resendTimer - 1 }

      case LoginActionTypes.SIGNUP_REQUEST:
         return {
            ...state,
            loading: true,
            error: null,
            email: action.payload.email, activeModal: 'smsCode',
            passwordResetFlow: false,
            password: action.payload.password,
            canResend: false, resendTimer: 30,
         };

      case LoginActionTypes.SIGNUP_SUCCESS:
         return { ...state, loading: false, error: null };

      case LoginActionTypes.SIGNUP_FAILED:
         return { ...state, loading: false, error: action.error, canResend: true, resendTimer: 0 };

      case LoginActionTypes.CONFIRM_CODE_REQUEST:
         return { ...state, loading: true, error: null };

      case LoginActionTypes.CONFIRM_CODE_SUCCESS:
         return {
            ...state, activeModal: null,
            passwordResetFlow: false, newPasswordFlow: false,
         };

      case LoginActionTypes.CONFIRM_CODE_FAILED:
         return { ...state, loading: false, error: action.error };

      case LoginActionTypes.SAVE_USER_DATA:
         return {
            ...state,
            user: {
               ...state.user,
               firstName: action.payload.name,
               lastName: action.payload.surname,
            }
         }

      case LoginActionTypes.SIGNIN_REQUEST:
         return {
            ...state, loading: true,
            error: null, email: action.payload.email,
            password: action.payload.password,
         }

      case LoginActionTypes.SIGNIN_SUCCESS:
         return {
            ...state, loading: false,
            error: null, activeModal: null,
            isAuthenticated: true,
         }

      case LoginActionTypes.SIGNIN_FAILED:
         return { ...state, loading: false, error: action.error }

      case LoginActionTypes.CODE_CHANGED:
         return { ...state, code: action.payload.code }

      case LoginActionTypes.NEW_PASSWORD_CHANGED:
         return { ...state, newPassword: action.payload.newPassword }

      case LoginActionTypes.FORGOT_PASSWORD_REQUEST:
         return { ...state, loading: true, email: action.payload.email, error: null }

      case LoginActionTypes.FORGOT_PASSWORD_SUCCESS:
         return { ...state, loading: false, error: null, activeModal: 'smsCode', passwordResetFlow: true }

      case LoginActionTypes.FORGOT_PASSWORD_FAILED:
         return { ...state, loading: false, error: action.error }

      case LoginActionTypes.CONFIRM_PASSWORD_REQUEST:
         return {
            ...state, loading: true, error: null, email: action.payload.email,
            code: action.payload.code, newPassword: action.payload.newPassword,
         }

      case LoginActionTypes.CONFIRM_PASSWORD_SUCCESS:
         return {
            ...state, loading: false, error: null, activeModal: null,
            isAuthenticated: true, passwordResetFlow: false, newPasswordFlow: false
         }

      case LoginActionTypes.CONFIRM_PASSWORD_FAILED:
         return { ...state, loading: false, error: action.error }

      case LoginActionTypes.SIGN_OUT_REQUEST:
         return { ...state }

      case LoginActionTypes.SIGN_OUT_SUCCESS:
         return initialState

      case LoginActionTypes.SIGN_OUT_FAILED:
         return { ...state, loading: false, error: action.error }

      case LoginActionTypes.GET_SESSION:
         return { ...state, isSessionLoading: true }

      case LoginActionTypes.GET_SESSION_SUCCESS:
         return {
            ...state, isSessionLoading: false, isAuthenticated: true,
            email: action.payload.email, userSession: action.payload.session, activeModal: null
         }

      case LoginActionTypes.GET_SESSION_FAILED:
         return { ...state, isSessionLoading: false, userSession: null, isAuthenticated: false, activeModal: null }

      default:
         return state;
   };
}; 